import { FC, useState } from 'react';
import { Button, ButtonStyles, ButtonWidth, FAIcon, IconStyle, useTranslation } from '@nextgen-web-framework/all';
import { CardType, PriceType, ProductCardComponent } from '../../Molecules';
import CheckoutCard from '../../CheckoutCard/CheckoutCard';
import { keyCheck } from '../../../utils';
import type { ProductsBundlePropsTypes, BundlePropType, SubscriptionCardType } from './types/propsTypes';
import { BG_COLOR, ProductBundleStyleType } from './types/enums';
import styles from './ProductsBundle.module.scss';

const ProductsBundle: FC<ProductsBundlePropsTypes> = ({
  bundle,
  className,
  productBundlStyleType,
  showSubscriptionDetails = false,
  subscriptionProducts,
  cardType = CardType.BUNDLE_CARD,
  showModifyBundleButton = false,
  onModifyBundleButtonClick,
  variantGroupId = '',
  restrictFromPdpDisplay,
  showPropsCollection: { showPvBvForReturns = false, showPriceForReturns = false } = {},
  showZeroAsFree,
  hideProductBundles,
}: ProductsBundlePropsTypes): JSX.Element => {
  const [viewBundleTile, setViewBundleTile] = useState<boolean | undefined>(!!hideProductBundles);

  const onBundleTileClick = () => {
    setViewBundleTile(!viewBundleTile);
  };

  const getClassName = () => {
    switch (productBundlStyleType) {
      case ProductBundleStyleType.POST_PURCHASE_PRODUCT_BUNDLE:
        return 'bundle-title-post-purchase-product';
      case ProductBundleStyleType.MANUAL_SOP_PRODUCT_BUNDLE:
        return 'bundle-title-manual-sop-product';
      default:
        return 'cpw-cart-bundle-tile';
    }
  };

  const t = useTranslation();
  const bundleButtonLabel = t('product.productBundle.dropdown');

  const showBundleCard = (bundleCard: BundlePropType[]) => (
    <div className={styles['card-container']}>
      {bundleCard?.length !== 0 &&
        bundleCard?.map((card: BundlePropType, index: number) => (
          <div className={styles['bundle-product-card-container']} key={`card${index + 1}`}>
            <ProductCardComponent
              {...card}
              productId={card.productId ?? ''}
              productSize={card.servingSize}
              productQuantity={card.quantity}
              derivedAvailabilityStatus={card.derivedAvailablityStatus}
              currency={''}
              showProductId={false}
              showPropertiesCollection={{
                showPromotionTag: card.isPromotionApplied,
              }}
              cardType={cardType}
              variantGroupId={variantGroupId}
              showLinkForProductCard={!restrictFromPdpDisplay}
              pointValue={showPvBvForReturns ? card.pointValue : undefined}
              businessVolume={showPvBvForReturns ? card.businessVolume : undefined}
              productPrice={showPriceForReturns ? card.productPrice : undefined}
              priceType={showPriceForReturns ? PriceType.PRICE_POST_PURCHASE : undefined}
              showZeroAsFree={showZeroAsFree}
            />
          </div>
        ))}
      {showModifyBundleButton && (
        <div className={styles['card-container-button']}>
          <Button
            onClick={onModifyBundleButtonClick}
            buttonStyle={ButtonStyles.OUTLINE}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            title="Modify Selection"
            backgroundColor={BG_COLOR.TRANSPARENT}
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`${styles[getClassName()]} ${viewBundleTile ? '' : styles['bundle-details']} ${className} ${
        showModifyBundleButton && styles['bundle-details-modify-btn']
      }`}
      data-testid={'cpw-cart-bundle-testid'}
      onClick={onBundleTileClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => keyCheck(e.code) && onBundleTileClick()}
      aria-label={bundleButtonLabel}
    >
      {viewBundleTile ? (
        <span className={styles['bundle-tile-dropdown']} data-testid={'bundle-dropdown-view'}>
          {showSubscriptionDetails ? t('product.ProductsBundle.viewSubscription') : t('product.ProductsBundle.view')}{' '}
          &nbsp;
          <FAIcon
            iconName={'chevron-down'}
            className={styles['bundle-tile-dropdown-icon']}
            iconStyle={IconStyle.REGULAR}
          />
        </span>
      ) : (
        <>
          <span className={styles['bundle-tile-dropdown']} data-testid={'bundle-dropdown-hide'}>
            {showSubscriptionDetails ? t('product.ProductsBundle.hideSubscription') : t('product.ProductsBundle.hide')}{' '}
            &nbsp;
            <FAIcon
              iconName={'chevron-up'}
              className={styles['bundle-tile-dropdown-icon']}
              iconStyle={IconStyle.REGULAR}
            />
          </span>

          {showSubscriptionDetails ? (
            <>
              {subscriptionProducts?.map((card: SubscriptionCardType, index: number) => (
                <div
                  key={`card${index + 1}`}
                  className={styles['subscription-container']}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  tabIndex={-1}
                  role="button"
                  aria-label={'bundle card'}
                >
                  <CheckoutCard
                    {...card}
                    productQuantity={card.quantity}
                    derivedAvailabilityStatus={card.derivedAvailablityStatus}
                    productPrice={card.price}
                    dateSubText={card.firstAvailableFutureDate}
                    variantGroupId={variantGroupId}
                    showProductId={false}
                    productSize={''}
                    showZeroAsFree={showZeroAsFree}
                  />

                  <div className={styles['bundle-container']}>{showBundleCard(card.bundle as BundlePropType[])}</div>
                </div>
              ))}
            </>
          ) : (
            <> {showBundleCard(bundle as BundlePropType[])} </>
          )}
        </>
      )}
    </div>
  );
};

export default ProductsBundle;
export type { ProductsBundlePropsTypes, BundlePropType, SubscriptionCardType };
export { ProductBundleStyleType, CardType };
